import React, { useState } from "react";
import { Tag, Popover, Modal, Steps, Alert, Button, Typography, Space } from "antd";
import {
  WhatsAppOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import moment from "moment";

const { Text } = Typography;

// Happy-path stages of a WhatsApp message, in order.
const STAGES = ["accepted", "sent", "delivered", "read"];

// Meta error codes we can explain with a specific cause + action
// (translations live under eventRegistrations.whatsapp.errors.<code>).
const KNOWN_ERRORS = [
  131049, 131026, 131047, 131030, 131048, 131056, 130429, 132000, 132001,
  132005, 132007, 132012, 132015, 132016, 133010, 190, 100,
];

const SUMMARY = {
  accepted: { color: "default", icon: <ClockCircleOutlined /> },
  sent: { color: "default", icon: <CheckCircleOutlined /> },
  delivered: { color: "blue", icon: <CheckCircleOutlined /> },
  read: { color: "green", icon: <CheckCircleOutlined /> },
  failed: { color: "red", icon: <CloseCircleOutlined /> },
  rejected: { color: "red", icon: <ExclamationCircleOutlined /> },
};

const fmt = (d) => (d ? moment.utc(d).local().format("DD MMM YYYY, HH:mm:ss") : null);

/**
 * Compact WhatsApp delivery badge for the registrations table.
 * Click the badge -> abstract status; click "View route" -> the full message route
 * (our server -> Meta -> WhatsApp network -> guest's phone -> read) with timestamps
 * and, if it stopped, where and why.
 */
const WhatsAppStatusCell = ({ record, tagLabel, tagColor }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // Controlled so the small status card can be closed from code: it must not stay on screen
  // once the full route dialog is opened.
  const [popoverOpen, setPopoverOpen] = useState(false);

  const status = record.whatsAppDeliveryStatus;
  if (!status) return null;

  const meta = SUMMARY[status] || SUMMARY.accepted;
  const history = record.whatsAppStatusHistory || [];
  const at = (s) => history.find((h) => h.status === s)?.at;
  const code = record.whatsAppErrorCode;
  const isError = status === "failed" || status === "rejected";

  const label = t(`eventRegistrations.whatsapp.status.${status}`);
  const badgeLabel = isError && code ? `${label} (${code})` : label;

  const explanation = (() => {
    if (!isError) return null;
    const key = KNOWN_ERRORS.includes(code) ? code : "unknown";
    return {
      cause: t(`eventRegistrations.whatsapp.errors.${key}.cause`),
      action: t(`eventRegistrations.whatsapp.errors.${key}.action`),
    };
  })();

  // Build the route. For a failure, mark the stage after the last one reached as the
  // point of failure; stages beyond it stay "waiting".
  const lastReachedIdx = status === "failed" || status === "rejected"
    ? STAGES.reduce((acc, s, i) => (at(s) ? i : acc), -1)
    : STAGES.indexOf(status);

  const items = [
    {
      title: t("eventRegistrations.whatsapp.route.queued"),
      description: t("eventRegistrations.whatsapp.route.queuedDesc"),
      status: status === "rejected" ? "error" : "finish",
      ...(status === "rejected" && {
        subTitle: fmt(at("rejected")),
        description: t("eventRegistrations.whatsapp.route.rejectedDesc"),
      }),
    },
    ...STAGES.map((s, i) => {
      let stepStatus = "wait";
      let failedHere = false;
      if (status === "rejected") {
        stepStatus = "wait";
      } else if (i <= lastReachedIdx) {
        stepStatus = "finish";
      } else if (status === "failed" && i === lastReachedIdx + 1) {
        stepStatus = "error";
        failedHere = true;
      }
      return {
        title: t(`eventRegistrations.whatsapp.route.${s}`),
        subTitle: fmt(at(s)),
        description: failedHere
          ? t("eventRegistrations.whatsapp.route.failedHere")
          : t(`eventRegistrations.whatsapp.route.${s}Desc`),
        status: stepStatus,
      };
    }),
  ];

  const summary = (
    <div style={{ maxWidth: 280 }}>
      <Space direction="vertical" size={6} style={{ width: "100%" }}>
        <Text strong>{label}</Text>
        {record.whatsAppStatusUpdatedAt && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t("eventRegistrations.whatsapp.updated", {
              time: fmt(record.whatsAppStatusUpdatedAt),
            })}
          </Text>
        )}
        {isError && explanation && (
          <Text type="danger" style={{ fontSize: 12 }}>
            {explanation.cause}
          </Text>
        )}
        <Button
          type="link"
          size="small"
          style={{ padding: 0 }}
          onClick={() => {
            setPopoverOpen(false);
            setOpen(true);
          }}
        >
          {t("eventRegistrations.whatsapp.viewRoute")}
        </Button>
      </Space>
    </div>
  );

  return (
    <>
      <Popover
        title={t("eventRegistrations.whatsapp.title")}
        content={summary}
        trigger="click"
        open={popoverOpen}
        onOpenChange={(next) => setPopoverOpen(next && !open)}
      >
        <Tag
          color={tagColor || meta.color}
          icon={<WhatsAppOutlined />}
          style={{ cursor: "pointer", marginInlineEnd: 0 }}
        >
          {tagLabel || badgeLabel}
        </Tag>
      </Popover>

      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        title={`${t("eventRegistrations.whatsapp.routeTitle")} — ${record.name}`}
        width={520}
      >
        {isError && explanation && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message={`${label}${code ? ` (${code})` : ""}`}
            description={
              <Space direction="vertical" size={4}>
                {record.whatsAppErrorMessage && (
                  <Text type="secondary">{record.whatsAppErrorMessage}</Text>
                )}
                <span>
                  <Text strong>{t("eventRegistrations.whatsapp.cause")}: </Text>
                  {explanation.cause}
                </span>
                <span>
                  <Text strong>{t("eventRegistrations.whatsapp.action")}: </Text>
                  {explanation.action}
                </span>
              </Space>
            }
          />
        )}
        <Steps direction="vertical" size="small" items={items} />
      </Modal>
    </>
  );
};

export default WhatsAppStatusCell;
